import { motion } from "framer-motion";
import { Search, Target, Wrench, Rocket, LineChart } from "lucide-react";
import { Eyebrow, Reveal, ease } from "./utils";

const steps = [
  {
    n: "01",
    icon: Search,
    title: "Identify Revenue Leaks",
    copy: "Find where leads, missed calls, quotes, customer data, reviews, and repeat-business opportunities are slipping through the cracks.",
  },
  {
    n: "02",
    icon: Target,
    title: "Prioritize the Best Opportunity",
    copy: "Focus first on the opportunity with the strongest potential impact based on goals, capacity, systems, and customer base.",
  },
  {
    n: "03",
    icon: Wrench,
    title: "Install the Right Systems",
    copy: "Strengthen lead response, quote follow-up, customer organization, pipeline visibility, and communication workflows.",
  },
  {
    n: "04",
    icon: Rocket,
    title: "Activate Growth Initiatives",
    copy: "Launch the right reactivation, nurture, referral, retention, seasonal, or lead-generation initiative when appropriate.",
  },
  {
    n: "05",
    icon: LineChart,
    title: "Track, Optimize, and Scale",
    copy: "Measure progress and create a more predictable path toward revenue goals.",
  },
];

export default function OpportunityEngine() {
  return (
    <section data-testid="opportunity-engine-section" className="relative overflow-hidden bg-white py-24 md:py-32">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="max-w-3xl">
          <Reveal><Eyebrow>The Apex Flow Method</Eyebrow></Reveal>
          <Reveal delay={1}>
            <h2 className="mt-6 font-serif text-4xl md:text-5xl tracking-tight text-brand-black leading-[1.08]">
              Growth Is Not One Tactic. It Is a <span className="gold-text">Connected System.</span>
            </h2>
          </Reveal>
          <Reveal delay={2}>
            <p className="mt-6 font-sans text-base md:text-lg leading-relaxed text-brand-black/65">
              Apex Flow does not recommend ads, automation, software, or a higher-tier package just because those tools
              are available. We first identify the strongest revenue opportunity inside your business—then build the
              right system around it.
            </p>
          </Reveal>
        </div>

        {/* Pathway */}
        <div className="relative mt-16">
          {/* connector line (desktop) */}
          <svg className="pointer-events-none absolute left-0 top-[44px] hidden lg:block w-full h-3" viewBox="0 0 1000 12" preserveAspectRatio="none" fill="none">
            <line x1="40" y1="6" x2="960" y2="6" stroke="#E8E8E6" strokeWidth="2" />
            <motion.line
              x1="40" y1="6" x2="960" y2="6"
              stroke="#C89A3D" strokeWidth="2" strokeLinecap="round"
              initial={{ pathLength: 0 }}
              whileInView={{ pathLength: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1.8, ease }}
            />
          </svg>

          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6 lg:gap-5">
            {steps.map((s, i) => (
              <Reveal key={s.n} delay={i} className="relative">
                <div className="group h-full">
                  <div className="relative z-10 mx-auto lg:mx-0 mb-5 flex h-[88px] w-[88px] items-center justify-center">
                    <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-white border border-brand-gray-soft shadow-luxe transition-all duration-300 group-hover:border-brand-gold group-hover:-translate-y-1">
                      <s.icon className="h-7 w-7 text-brand-gold" strokeWidth={1.5} />
                    </div>
                  </div>
                  <div className="rounded-2xl glass-panel p-6 h-full transition-all duration-300 group-hover:-translate-y-1 group-hover:shadow-luxe-gold">
                    <span className="font-mono text-xs font-medium tracking-widest text-brand-gold">{s.n}</span>
                    <h3 className="mt-2 font-sans text-lg font-semibold tracking-tight text-brand-black">{s.title}</h3>
                    <p className="mt-2.5 font-sans text-sm leading-relaxed text-brand-black/60">{s.copy}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        {/* Dark callout */}
        <Reveal delay={1} className="mt-14">
          <div className="relative overflow-hidden rounded-2xl bg-brand-black px-8 py-10 md:px-14 md:py-12 text-center">
            <div className="pointer-events-none absolute inset-0 grain-overlay opacity-[0.05]" />
            <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 -bottom-16 h-40 w-[420px] rounded-full bg-brand-gold/15 blur-[90px]" />
            <p className="relative font-serif text-2xl md:text-4xl tracking-tight text-white">
              The right next move is not always <span className="gold-text">more marketing.</span>
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
